import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { CREATE_WORK_ORDER } from '../graphql/mutations';
import { BULK_UPDATE_CUSTOM_WORK_ORDER_FLAGS } from '../graphql/work-order-flags';
import WorkOrderForm from '../components/work-orders/WorkOrderForm';
import { useAuth } from '../contexts/AuthContext';

export default function CreateWorkOrder() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [errorMessage, setErrorMessage] = useState('');

  const [createWorkOrder, { loading }] = useMutation(CREATE_WORK_ORDER, {
    onError: (error) => {
      console.error('Error creating work order:', error);
      // Check for specific error messages
      if (error.message?.includes('property')) {
        setErrorMessage('Error with property selection. Please select a valid property and try again.');
      } else if (error.message?.includes('reservation')) {
        setErrorMessage('Error with reservation. Please try again without a reservation.');
      } else {
        setErrorMessage(error.message || 'An error occurred while creating the work order. Please try again.');
      }
    },
    // Ensure we get fresh data
    fetchPolicy: 'no-cache'
  });
  const [bulkUpdateFlags] = useMutation(BULK_UPDATE_CUSTOM_WORK_ORDER_FLAGS);

  const handleSubmit = async (formData) => {
    setErrorMessage(''); // Clear previous error
    
    try {
      // Validate that propertyId is a valid UUID format
      if (!formData.propertyId || !formData.propertyId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
        setErrorMessage('Please select a valid property');
        console.error('Invalid propertyId:', formData.propertyId);
        return false;
      }
      
      if (!formData.description || formData.description.trim() === '') {
        setErrorMessage('Description is required');
        return false;
      }
      
      // Create a clean copy of form data with only required fields
      const workOrderData = {
        propertyId: formData.propertyId.trim(),
        description: formData.description.trim(),
        isUrgent: !!formData.isUrgent,
        isAfterHours: !!formData.isAfterHours,
        isFeedback: !!formData.isFeedback,
        workOrderType: formData.workOrderType,
        submittedById: currentUser?.id
      };
      
      // Only add non-empty optional fields
      if (formData.photos && formData.photos.length > 0) {
        workOrderData.photos = formData.photos;
      }

      // Add visibility roles if provided
      if (formData.visibilityRoles && formData.visibilityRoles.length > 0) {
        workOrderData.visibilityRoles = formData.visibilityRoles;
      }

      // Add assignment data if provided
      if (formData.assignedTeamId) {
        workOrderData.assignedTeamId = parseInt(formData.assignedTeamId, 10);
      } else if (formData.assignedToId) {
        workOrderData.assignedToId = parseInt(formData.assignedToId, 10);
      }

      if (formData.assignedRoles && formData.assignedRoles.length > 0) {
        workOrderData.assignedRoles = formData.assignedRoles;
      }

      if (formData.reservationId) {
        workOrderData.reservationId = formData.reservationId;
      }
      workOrderData.guestAlertsEnabled = !!formData.guestAlertsEnabled;
      if (formData.guestAlertsEnabled) {
        if (formData.guestAlertDescription?.trim()) {
          workOrderData.guestAlertDescription = formData.guestAlertDescription.trim();
        }
        if (formData.guestAlertUseContactOverride && formData.guestAlertEmailOverride?.trim()) {
          workOrderData.guestAlertEmailOverride = formData.guestAlertEmailOverride.trim();
        }
        if (formData.guestAlertUseContactOverride && formData.guestAlertPhoneOverride?.trim()) {
          workOrderData.guestAlertPhoneOverride = formData.guestAlertPhoneOverride.trim();
        }
      }
      
      console.log('Submitting work order with data:', workOrderData);
      
      const result = await createWorkOrder({
        variables: {
          input: workOrderData
        }
      });

      const workOrderId = result?.data?.createWorkOrder?.id;
      if (workOrderId && formData.workOrderFlagTemplateIds?.length > 0) {
        await bulkUpdateFlags({
          variables: {
            input: {
              workOrderId,
              flags: formData.workOrderFlagTemplateIds.map((templateId, index) => ({
                workOrderId,
                templateId,
                flagType: 'custom',
                flagValue: 'template',
                sortOrder: index
              }))
            }
          }
        });
      }

      if (workOrderId) {
        navigate(`/dashboard/work-orders/${workOrderId}`);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error in handleSubmit:', error);
      setErrorMessage('An unexpected error occurred. Please try again.');
      return false;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <Link
          to="/dashboard/work-orders"
          className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium border border-white/30 text-white hover:bg-white/10 lg:border-gray-300 lg:text-gray-700 lg:bg-white lg:hover:bg-gray-50 lg:hover:border-gray-400 shadow-sm transition-colors"
        >
          <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          All Work Orders
        </Link>
        <h1 className="text-xl font-semibold text-white lg:text-gray-900 flex items-center">
          <svg className="h-5 w-5 mr-1.5 text-blue-300 lg:text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          Create New Work Order
        </h1>
      </div>

      {errorMessage && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md shadow-sm">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-semibold text-red-800">Error</h3>
              <div className="mt-1 text-sm text-red-700">
                <p>{errorMessage}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow-md overflow-hidden rounded-xl border-t-4 border-blue-600">
        <div className="px-4 py-5 sm:p-6">
          <WorkOrderForm 
            onSubmit={handleSubmit} 
            isLoading={loading} 
          />
        </div>
      </div>
    </div>
  );
}
